import {SectionIntro, Section, Box, Grid} from '@primer/react-brand/esm'
import type {FeaturedContentProps} from './types'
import {renderWithoutParagraphs} from '../../../utils'
import {FlexSuiteRiverAccordion} from '../../../components/FlexSuiteRiverAccordion'
import {FlexSuiteRiverStack} from '../../../components/FlexSuiteRiverStack'
import {FlexSuiteRiverBreakout} from '../../../components/FlexSuiteRiverBreakout'
import {FlexSuiteRiverBreakoutTabs} from '../../../components/FlexSuiteRiverBreakoutTabs'
import {FlexSuiteRiverFootsteps} from '../../../components/FlexSuiteRiverFootsteps'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {
  FlexSuiteTestimonialQuote,
  FlexSuiteTestimonialStat,
  FlexSuiteTestimonialBanner,
} from '../../../components/FlexSuiteTestimonial'
import baseStyles from '../../../styles/base.module.css'
import {BrandedDivider} from '../../../components/BrandedDivider'

const FeaturedContent = ({
  settings,
  featureHeading,
  featureLabel,
  featureDescription,
  featureLink,
  featureDetailsOne,
  featureRiverAccordion,
  featureRiverStack,
  featureDetailsTwo,
  featureRiverBreakout,
  featureBreakoutTabs,
  featureDetailsThree,
  featureDetailsThreeHeading,
  featureFootstepsLabel,
  featureFootstepsLink,
  featureFootstepsCards,
  featureLargeCards,
  featureDetailsTestimonial,
  featureTestimonialQuote,
  featureTestimonialName,
  featureTestimonialTitle,
  featureTestimonialLink,
  featureTestimonialLabel,
  featureTestimonialLogo,
  featureTestimonialStats,
  featureTestimonialImage,
}: FeaturedContentProps) => {
  const brand = settings?.fields.brand
  return (
    <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
      <Box
        paddingBlockEnd={{
          narrow: 'spacious',
          regular: 64,
        }}
        paddingBlockStart={{
          narrow: 'spacious',
          regular: 64,
        }}
      >
        <Grid>
          <Grid.Column>
            <SectionIntro align="center" fullWidth>
              {featureLabel && <SectionIntro.Label>{featureLabel}</SectionIntro.Label>}
              {featureHeading && (
                <SectionIntro.Heading size="3">{renderWithoutParagraphs(featureHeading)}</SectionIntro.Heading>
              )}
              {featureDescription && (
                <SectionIntro.Description>{renderWithoutParagraphs(featureDescription)}</SectionIntro.Description>
              )}
              {featureLink?.fields.href && (
                <SectionIntro.Link href={featureLink.fields.href}>{featureLink.fields.text}</SectionIntro.Link>
              )}
            </SectionIntro>
          </Grid.Column>
        </Grid>
      </Box>
      {featureDetailsOne === 'Accordion' && featureRiverAccordion ? (
        <Box
          marginBlockStart="condensed"
          paddingBlockEnd="spacious"
          paddingBlockStart="spacious"
          className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
        >
          <Grid>
            <Grid.Column>
              <FlexSuiteRiverAccordion items={featureRiverAccordion} />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
      {featureDetailsOne === 'Stack' && featureRiverStack ? (
        <Section paddingBlockStart="none" paddingBlockEnd="none">
          <FlexSuiteRiverStack items={featureRiverStack} brand={brand} showDither />
        </Section>
      ) : null}
      {featureDetailsTwo === 'Breakout' && featureRiverBreakout ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakout {...featureRiverBreakout.fields} />
          </Grid.Column>
        </Grid>
      ) : null}
      {featureDetailsTwo === 'Carousel' && featureBreakoutTabs ? (
        <Grid>
          <Grid.Column>
            <FlexSuiteRiverBreakoutTabs items={featureBreakoutTabs} />
          </Grid.Column>
        </Grid>
      ) : null}
      {featureDetailsThree === 'Footsteps' && featureFootstepsCards ? (
        <Box
          className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
          marginBlockStart={48}
          paddingBlockStart={48}
          paddingBlockEnd={48}
        >
          <FlexSuiteRiverFootsteps
            heading={featureDetailsThreeHeading}
            label={featureFootstepsLabel}
            cards={featureFootstepsCards}
            cta={featureFootstepsLink && featureFootstepsLink.fields}
          />
        </Box>
      ) : null}
      {featureDetailsThree === 'Large Cards' && featureLargeCards ? (
        <Box marginBlockStart={48} paddingBlockStart={48} paddingBlockEnd={48}>
          <FlexSuiteLargeCards heading={featureDetailsThreeHeading} cards={featureLargeCards} />
        </Box>
      ) : null}
      {featureDetailsTestimonial === 'Quote' && featureTestimonialQuote ? (
        <FlexSuiteTestimonialQuote
          quote={featureTestimonialQuote}
          name={featureTestimonialName}
          title={featureTestimonialTitle}
          link={featureTestimonialLink && featureTestimonialLink.fields}
          logo={featureTestimonialLogo}
        />
      ) : null}
      {featureDetailsTestimonial === 'Statistics' &&
      featureTestimonialStats &&
      featureTestimonialQuote &&
      featureTestimonialLink &&
      featureTestimonialImage ? (
        <Box marginBlockStart={48}>
          <FlexSuiteTestimonialStat
            quote={featureTestimonialQuote}
            name={featureTestimonialName}
            title={featureTestimonialTitle}
            link={featureTestimonialLink.fields}
            label={featureTestimonialLabel}
            image={featureTestimonialImage}
            stats={featureTestimonialStats}
          />
        </Box>
      ) : null}
      {featureDetailsTestimonial === 'Brand Banner' && featureTestimonialQuote ? (
        <Box paddingBlockEnd="spacious" paddingBlockStart="spacious">
          <FlexSuiteTestimonialBanner
            quote={featureTestimonialQuote}
            link={featureTestimonialLink && featureTestimonialLink.fields}
            logo={featureTestimonialLogo}
            image={featureTestimonialImage}
          />
        </Box>
      ) : null}
      {brand === 'Copilot' ? (
        <Box className={baseStyles.gridFrameBottom} data-testid="featured-content-branded-divider">
          <Grid>
            <Grid.Column>
              <BrandedDivider brand={brand} />
            </Grid.Column>
          </Grid>
        </Box>
      ) : null}
    </Section>
  )
}
export {FeaturedContent}
